import { useReactiveVar } from '@apollo/client/react';
import Drawer from 'components/Drawer/Drawer';
import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useIsOfficeModeEnabled from 'hooks/useIsOfficeModeEnabled/useIsOfficeModeEnabled';
import usePermissions from 'hooks/usePermissions/usePermissions';
import useSortingUserGroupsTable from 'hooks/useSorting/useSortingAccessSettingsTable/useSortingAccessSettingsTable';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import useVisitorsGroups from 'hooks/useVisitorsGroups/useVisitorsGroups';
import { kebabCase } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { VisitorsGroupsState } from 'state/types';
import { authVar } from 'state/vars';
import styled from 'styled-components';
import TableCell from 'ui/atoms/TableCell/TableCell';
import TableHeader from 'ui/atoms/TableHeader/TableHeader';
import Search from 'ui/molecules/Search/Search';
import TableHeaderRow from 'ui/molecules/TableHeaderRow/TableHeaderRow';
import TableRow from 'ui/molecules/TableRow/TableRow';
import Table from 'ui/organisms/Table/Table';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import PageContent from 'ui/templates/PageContent/PageContent';
import { StickyToTop } from 'ui/templates/StickyToTop/StickyToTop';
import ArrayUtil from 'utils/Array/Array.util';
import { filterBySearchTerm } from 'utils/FilterBySearchTerm/FilterBySearchTerm.util';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import getVisitorGroupIcon from 'utils/VisitorGroupIcon/VisitorGroupIcon.util';
import { createAccessTabs } from '../accessTabsConst';

const SearchWrapper = styled.div`
  margin: 1rem 0 2rem 0;
  width: 14.875rem;
`;

export const createHeaderAccessSettingsLeftTable = (translation: Translation) => [translation.groups];
export const createHeaderAccessSettingsRightTable = (translation: Translation) => [translation.access_settings];

const AccessSettings: React.FC = () => {
  const isAllowQrInvitesEnabled = process.env.REACT_APP_IS_ALLOW_QR_INVITES_ENABLED === 'true';
  const { translation } = useTranslation();
  const { user } = useReactiveVar(authVar);
  const accessTabs = createAccessTabs(translation);
  const headerAccessSettingsLeftTable = createHeaderAccessSettingsLeftTable(translation);
  const headerAccessSettingsRightTable = createHeaderAccessSettingsRightTable(translation);
  const [filterStr, setFilterStr] = useState('');
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { visitorsGroups, loading: visitorGroupsLoading } = useVisitorsGroups({
    handleFetchError,
    fetchPolicy: 'network-only'
  });

  const {
    checkIsOfficeModeEnabledForVisitorGroup,
    loading: visitorGroupsWithIsOfficeModeEnabledLoading,
    updateAccessGrantIsOfficeModeEnabled,
    updateAccessGrantIsOfficeModeEnabledMutationLoading
  } = useIsOfficeModeEnabled({ handleFetchError });

  const { setIsQrCodeVisitInviterGroup, setIsQrCodeVisitInvitorGroupLoading } = usePermissions({
    handleFetchError
  });

  const skeletonArray = ArrayUtil.SkeletonArray();
  const [leftTableSelectedRowGroup, setLeftTableSelectedRowGroup] = useState<VisitorGroup>();
  const { handleSortingOnClick, handleSortingIcon } = useSortingUserGroupsTable(translation);

  const getFilterData = useCallback(
    (): VisitorsGroupsState => filterBySearchTerm(visitorsGroups, filterStr, (element) => [element.name]),
    [filterStr, visitorsGroups]
  );

  useEffect(() => {
    if (
      filterStr &&
      leftTableSelectedRowGroup &&
      getFilterData().find((item) => item === leftTableSelectedRowGroup) === undefined
    )
      setLeftTableSelectedRowGroup(undefined);
  }, [filterStr, getFilterData, leftTableSelectedRowGroup]);

  const handleLeftTableOnRowClick = (group: VisitorGroup) => {
    if (leftTableSelectedRowGroup?.externalRef !== group.externalRef) setLeftTableSelectedRowGroup(group);
    else setLeftTableSelectedRowGroup(undefined);
  };

  const handleRightTableOnOfficeModeClick = () => {
    if (!updateAccessGrantIsOfficeModeEnabledMutationLoading && leftTableSelectedRowGroup)
      updateAccessGrantIsOfficeModeEnabled(leftTableSelectedRowGroup.id);
  };

  const handleRightTableAllowQrCodeInvitesClick = async () => {
    if (!setIsQrCodeVisitInvitorGroupLoading && leftTableSelectedRowGroup !== undefined) {
      const updatedGroup = await setIsQrCodeVisitInviterGroup(
        leftTableSelectedRowGroup.id,
        !leftTableSelectedRowGroup.isQrCodeVisitInviterGroup
      );
      if (updatedGroup)
        setLeftTableSelectedRowGroup({
          ...leftTableSelectedRowGroup,
          isQrCodeVisitInviterGroup: !leftTableSelectedRowGroup.isQrCodeVisitInviterGroup
        });
    }
  };

  const getIsQrCodeVisitInviterCapableGroup = (userGroup: VisitorGroup | undefined) => {
    return Boolean(
      userGroup && (userGroup.type === VisitorGroupType.AzureAd || userGroup.type === VisitorGroupType.CoContractors)
    );
  };

  return (
    <PageContent title="ATLAS" titleId="location" tabs={accessTabs} id="access-settings-page">
      <StickyToTop>
        <ComponentWrapper alignItems="center" justifyContent="end" gap="0.5rem">
          <SearchWrapper>
            <Search onChange={(event) => setFilterStr(event.target.value)} value={filterStr} />
          </SearchWrapper>
        </ComponentWrapper>
      </StickyToTop>
      <ComponentWrapper width="calc(100vw - 25rem)" height="calc(100vh - 24rem)" margin="0 1rem 0 0" gap="1rem">
        <ComponentWrapper flex="0 0 30%">
          <Table
            header={
              <TableHeaderRow placeForAvatarOrSwitchOrCheckbox placeForArrow>
                <TableHeader
                  headerText={headerAccessSettingsLeftTable[0]}
                  id={kebabCase(`header-${headerAccessSettingsLeftTable[0]}`)}
                  flex="0 0 100%"
                  iconSorting={handleSortingIcon(headerAccessSettingsLeftTable[0])}
                  onClick={() => handleSortingOnClick(headerAccessSettingsLeftTable[0])}
                />
              </TableHeaderRow>
            }
            headerId="ad-groups-access-settings-header"
            rowsWrapperId="ad-groups-access-settings-list"
          >
            {visitorGroupsLoading || visitorGroupsWithIsOfficeModeEnabledLoading
              ? skeletonArray.map((_, index) => (
                  <TableRow
                    beforeSwitchSkeleton
                    id={`row-skeleton-${index}`}
                    marginAfterContent
                    key={`skeletonTableLeftColumnRow-${_.id}`}
                  >
                    <TableCell isLoading firstLineText="" flex="0 0 100%" />
                  </TableRow>
                ))
              : getFilterData().map((userGroup, index) => (
                  <TableRow
                    hoverEffect
                    beforeContentIconProps={{
                      name: getVisitorGroupIcon.getVisitorGroupIcon(userGroup.type as VisitorGroupType),
                      width: 24,
                      height: 24,
                      viewBox: '0 0 24 24',
                      stroke: 'transparent',
                      color: 'lTextHigh'
                    }}
                    id={`row-${index}`}
                    key={userGroup.id}
                    onClick={() => handleLeftTableOnRowClick(userGroup)}
                    selected={userGroup.externalRef === leftTableSelectedRowGroup?.externalRef}
                  >
                    <TableCell firstLineText={userGroup.name} firstLineId={`row-${index}-name`} flex="0 0 100%" />
                  </TableRow>
                ))}
          </Table>
        </ComponentWrapper>
        <ComponentWrapper flex="0 0 70%" width="5rem">
          <Table
            header={
              <TableHeaderRow>
                <TableHeader
                  headerText={headerAccessSettingsRightTable[0]}
                  id={kebabCase(`header-${headerAccessSettingsRightTable[0]}`)}
                  flex="0 0 100%"
                />
              </TableHeaderRow>
            }
            headerId="permission-header"
            rowsWrapperId="permission-list"
          >
            {visitorGroupsLoading || visitorGroupsWithIsOfficeModeEnabledLoading ? (
              <>
                <TableRow id="row-skeleton-0" marginAfterContent key="skeletonTableRightColumnRow-0">
                  <TableCell isLoading firstLineText="" flex="0 0 100%" />
                </TableRow>
                <TableRow id="row-skeleton-1" marginAfterContent key="skeletonTableRightColumnRow-1">
                  <TableCell isLoading firstLineText="" flex="0 0 100%" />
                </TableRow>
              </>
            ) : (
              <>
                <TableRow
                  id="office-mode-option"
                  key="row-permission-office-mode"
                  afterSwitchProps={{
                    checked:
                      leftTableSelectedRowGroup &&
                      checkIsOfficeModeEnabledForVisitorGroup(leftTableSelectedRowGroup.id),
                    disabled: leftTableSelectedRowGroup === undefined
                  }}
                  onClick={handleRightTableOnOfficeModeClick}
                  marginAfterContent
                  disabled={leftTableSelectedRowGroup === undefined}
                >
                  <ComponentWrapper maxWidth="36rem">
                    <TableCell
                      firstLineText={translation.allow_office_mode}
                      firstLineId="row-permission-office-mode-firstLine"
                      secondLineText={translation.allow_users_to_enable_office_mode_on_available_locks}
                      secondLineId="row-permission-office-mode-second-line"
                      flex="0 0 100%"
                    />
                  </ComponentWrapper>
                </TableRow>
                {isAllowQrInvitesEnabled && (
                  <TableRow
                    id="qr-code-visit-inviter-role"
                    key="row-qr-code-visit-inviter-role"
                    afterSwitchProps={{
                      checked: leftTableSelectedRowGroup?.isQrCodeVisitInviterGroup,
                      disabled: getIsQrCodeVisitInviterCapableGroup(leftTableSelectedRowGroup)
                    }}
                    onClick={
                      !getIsQrCodeVisitInviterCapableGroup(leftTableSelectedRowGroup)
                        ? undefined
                        : handleRightTableAllowQrCodeInvitesClick
                    }
                    marginAfterContent
                    disabled={!getIsQrCodeVisitInviterCapableGroup(leftTableSelectedRowGroup)}
                  >
                    <ComponentWrapper maxWidth="36rem">
                      <TableCell
                        firstLineText={translation.allow_qr_invites}
                        firstLineId="row-permission-access-by-reservation-firstLine"
                        secondLineText={translation.allows_users_in_this_group_to_create_qr_code_invites}
                        secondLineId="row-permission-access-by-reservation-second-line"
                        flex="0 0 100%"
                      />
                    </ComponentWrapper>
                  </TableRow>
                )}
              </>
            )}
          </Table>
        </ComponentWrapper>
      </ComponentWrapper>
      <Drawer />
    </PageContent>
  );
};

export default AccessSettings;
